import React, { useState } from 'react'
import { Stack, TextColors, Typography } from '@sureco/design-system'
import {
  Button,
  DatePicker,
  GetProps,
  Modal,
} from '@surecompanies/core_components'
import dayjs from 'dayjs'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import RHFDatePicker from 'enrollment-platform/components/form/hookform/DatePicker/RHFDatePicker'
import { UploadResultSchema } from './upload-result.schema'
import RHFDragger from 'enrollment-platform/components/form/hookform/Dragger/RHFDragger'
import useUploadResult from './hooks/useUploadResult'

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>
type FormData = z.infer<typeof UploadResultSchema>

export default function UploadResultModal({
  isOpen,
  onCancel,
  id,
}: {
  isOpen: boolean
  onCancel: () => void
  id: number | null
}) {
  const { uploadResult, isPending } = useUploadResult(id)
  const [file, setFile] = useState<File | null>(null)
  const { handleSubmit, control } = useForm<FormData>({
    resolver: zodResolver(UploadResultSchema),
  })
  if (!isOpen || !id) return null
  const disabledDate: RangePickerProps['disabledDate'] = (current) =>
    current && current > dayjs().endOf('day')

  const onUploadFile = (file: File) => {
    setFile(file)
    return false
  }

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      maskClosable={false}
      footer={
        <Stack direction="row" justify="center" space={10}>
          <Button
            type="primary"
            variant="outlined"
            color="default"
            onClick={onCancel}
          >
            Close
          </Button>
          <Button
            type="primary"
            loading={isPending}
            onClick={handleSubmit(async (data) => {
              if (!file) return
              await uploadResult({
                file,
                uploadedDate: data.uploadedDate,
              })
              onCancel()
            })}
          >
            Upload
          </Button>
        </Stack>
      }
    >
      <Stack space={10}>
        <Typography size={16}>Upload Result</Typography>
        <Stack space={10}>
          <RHFDatePicker
            name="uploadedDate"
            control={control}
            disabledDate={disabledDate}
          />
          <RHFDragger
            name="file"
            control={control}
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple={false}
            beforeUpload={onUploadFile}
            onRemove={() => setFile(null)}
            hint={
              <Typography size={12} color={TextColors.primary}>
                Support for a single XLSX file.
              </Typography>
            }
          ></RHFDragger>
          {!file && (
            <Typography size={12} color={TextColors.error}>
              Please upload a file
            </Typography>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
